import type { BookPlanId } from '@kitup/book-schema';
import type { BrandConfig, StorefrontPageProps } from '@kitup/brand';
import { useEffect, useRef, useState, type CSSProperties, type JSX, type RefObject } from 'react';
import drawnBiscuit from './assets/drawn-biscuit.webp';
import drawnDad from './assets/drawn-dad.webp';
import drawnLily from './assets/drawn-lily.webp';
import drawnMom from './assets/drawn-mom.webp';
import drawnMoonbun from './assets/drawn-moonbun.webp';
import photoBiscuit from './assets/photo-biscuit.jpg';
import photoDad from './assets/photo-dad.jpg';
import photoLily from './assets/photo-lily.jpg';
import photoMom from './assets/photo-mom.jpg';
import photoMoonbun from './assets/photo-moonbun.jpg';
import spreadPoster from './assets/spread.webp';
import spreadVideo from './assets/spread.mp4';
import { POPULAR_PLAN, cheapestPrice, planFacts } from './facts.js';
import { Footer, Header } from './Header.js';

/**
 * The cast of the example book, "Puddle Hop". Nobody here is a real person: the photos are
 * generated test uploads (`fixtures/uploads`), and the drawings are cut from the book's
 * first spread, which was made from exactly these photos. Dad is not in the book: his
 * drawing is cut from a copy of that spread with him added from his photos, made for this
 * page only.
 */
const CAST = [
  { name: 'Lily', photo: photoLily, drawn: drawnLily, colour: '--ta-orange', tilt: '-4deg' },
  { name: 'Biscuit', photo: photoBiscuit, drawn: drawnBiscuit, colour: '--ta-sun', tilt: '3deg' },
  { name: 'Moonbun', photo: photoMoonbun, drawn: drawnMoonbun, colour: '--ta-pink', tilt: '-2deg' },
  { name: 'Mom', photo: photoMom, drawn: drawnMom, colour: '--ta-sky', tilt: '3deg' },
  { name: 'Dad', photo: photoDad, drawn: drawnDad, colour: '--ta-grass', tilt: '-3deg' },
] as const;

/** When the drawing of each card, once it has landed, turns face up. */
const TURN_AFTER_MS = 1900;
const TURN_STAGGER_MS = 220;

function CastCard({
  member,
  index,
}: {
  readonly member: (typeof CAST)[number];
  readonly index: number;
}): JSX.Element {
  const [showsPhoto, setShowsPhoto] = useState(true);
  const [hopping, setHopping] = useState(false);
  const touched = useRef(false);

  useEffect(() => {
    const timer = setTimeout(
      () => {
        if (!touched.current) setShowsPhoto(false);
      },
      TURN_AFTER_MS + index * TURN_STAGGER_MS,
    );
    return () => clearTimeout(timer);
  }, [index]);

  const flip = (): void => {
    touched.current = true;
    setShowsPhoto((value) => !value);
    setHopping(false);
    requestAnimationFrame(() => setHopping(true));
  };

  return (
    <li>
      <button
        type="button"
        className="ta-card"
        style={{ '--i': index } as CSSProperties}
        aria-pressed={showsPhoto}
        aria-label={`Show ${member.name}’s photo`}
        onClick={flip}
      >
        <span
          className="ta-flip"
          data-hop={hopping ? 'true' : 'false'}
          onAnimationEnd={() => setHopping(false)}
        >
          <span className="ta-face ta-photo" style={{ '--r': member.tilt } as CSSProperties}>
            <img src={member.photo} alt="" />
            <b>photo</b>
          </span>
          <span
            className="ta-face ta-drawn"
            style={{ '--c': `var(${member.colour})` } as CSSProperties}
          >
            <img src={member.drawn} alt="" />
            <b>{member.name}</b>
          </span>
        </span>
      </button>
    </li>
  );
}

/** Scales the fixed-size bouncing path down to the width it has. */
function useTrackScale(): RefObject<HTMLDivElement | null> {
  const track = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = track.current;
    // Without it (jsdom, very old browsers) the path keeps its full size.
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      element.style.setProperty('--k', String(Math.min(1, element.clientWidth / 1100)));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return track;
}

const HOP_PATH =
  'M -60 20 Q 60 -60 180 262 Q 365 -120 550 262 Q 735 -120 920 262 Q 1010 110 1100 150';

export function LandingPage({
  brand,
  page,
}: {
  readonly brand: BrandConfig;
  readonly page: StorefrontPageProps;
}): JSX.Element {
  const { Link, user } = page;
  const createBook = user ? '/app' : '/register';
  const track = useTrackScale();
  const plans = planFacts(brand);
  const summaries: Readonly<Record<BookPlanId, (characters: number) => string>> = {
    book: (characters) => `Illustrated spreads and covers, up to ${characters} characters.`,
    live_book: (characters) =>
      `Every picture moves. A longer story, up to ${characters} characters.`,
    gift_book: (characters) =>
      `Read aloud in a familiar voice, high-quality print, up to ${characters} characters.`,
  };
  const tierColour: Readonly<Record<string, string>> = {
    book: 'var(--ta-orange)',
    live_book: '#fff',
    gift_book: 'var(--ta-grass)',
  };

  return (
    <div className="ta-storefront">
      <a href="#content" className="ta-skip">
        Skip to content
      </a>
      <Header page={page} current="landing" />

      <main id="content">
        <section className="ta-hero">
          <p className="ta-kicker">A picture book starring your kid</p>
          <h1 className="ta-h1">
            The one they’ll shout
            <br />
            <span className="ta-again">
              <span className="ta-sr">“Again!”</span>
              {['“A', 'G', 'A', 'I', 'N', '!”'].map((letter, index) => (
                <span key={index} style={{ '--i': index } as CSSProperties} aria-hidden="true">
                  {letter}
                </span>
              ))}
            </span>{' '}
            at
          </h1>
          <p className="ta-hero-lead">
            Three photos of each character — your kids, the grown-ups, the toy that goes everywhere.
            We draw them into a real picture book.
          </p>
          <div className="ta-hero-actions">
            <Link to={createBook} className="ta-btn">
              Make their book
            </Link>
            <Link to={brand.landingExamplePath} className="ta-btn ta-btn-sun">
              Peek inside a book
            </Link>
          </div>
          <small className="ta-hero-note">
            First spread free · then from {cheapestPrice(brand)}, once
          </small>

          <ul className="ta-cast" aria-label="The cast of an example book">
            {CAST.map((member, index) => (
              <CastCard key={member.name} member={member} index={index} />
            ))}
          </ul>
          <p className="ta-cast-note">Tap a card: photo on one side, drawing on the other.</p>
        </section>

        <section className="ta-pageone" aria-labelledby="ta-landed">
          {/*
           * An open picture book, not a framed video: a hard cover under a
           * stack of pages, the fold down the middle, and the story's words
           * on the page the way the product sets them.
           */}
          <figure className="ta-open-book" aria-labelledby="ta-open-words">
            <div className="ta-open-cover" aria-hidden="true" />
            <div className="ta-open-edges" aria-hidden="true" />
            <div className="ta-open-spread">
              <video
                src={spreadVideo}
                poster={spreadPoster}
                aria-label="An animated spread from a personalized book"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
              />
              <p className="ta-open-words" aria-hidden="true">
                <span>
                  Lily jumped into the biggest puddle in the backyard. Splash! Biscuit jumped in
                  right after her.
                </span>
              </p>
              <span className="ta-open-shade" aria-hidden="true" />
            </div>
            <figcaption id="ta-open-words" className="ta-sr">
              Lily jumped into the biggest puddle in the backyard. Splash! Biscuit jumped in right
              after her.
            </figcaption>
          </figure>
          <div>
            <h2 id="ta-landed" className="ta-h2">
              …and here’s
              <br />
              where they <span className="ta-o">landed.</span>
            </h2>
            <p className="ta-lede">
              The first page of Lily’s book: the same girl, the same dog, the same bunny on the
              swing, Mom on the porch. In a Living Book, every page moves like this.
            </p>
            <span className="ta-badge">Made from the five photos above</span>
          </div>
        </section>

        <section className="ta-hops" aria-labelledby="ta-hops">
          <h2 id="ta-hops" className="ta-h2">
            Three hops to <span className="ta-o">the end</span>
          </h2>
          <div className="ta-track" ref={track} aria-hidden="true">
            <div className="ta-track-box">
              <svg viewBox="0 0 1100 300">
                <path d={HOP_PATH} />
              </svg>
              <span
                className="ta-pad"
                style={{ left: 180, '--c': 'var(--ta-orange)' } as CSSProperties}
              />
              <span
                className="ta-pad"
                style={{ left: 550, '--c': 'var(--ta-sky)' } as CSSProperties}
              />
              <span
                className="ta-pad"
                style={{ left: 920, '--c': 'var(--ta-grass)' } as CSSProperties}
              />
              <span className="ta-ball" />
            </div>
          </div>
          <ol className="ta-steps">
            <li>
              <h3>Meet the cast</h3>
              <p>Three photos, a name and a personality each. You approve every drawing.</p>
            </li>
            <li>
              <h3>Say what happens</h3>
              <p>
                A few lines in your own words become the spreads — or write every line yourself.
              </p>
            </li>
            <li>
              <h3>Read it together</h3>
              <p>On a tablet, the TV or printed. In English, French or Spanish.</p>
            </li>
          </ol>
        </section>

        <section className="ta-grownups" aria-labelledby="ta-grownups">
          <h2 id="ta-grownups" className="ta-h2">
            The bouncy part is for them. <span className="ta-o">This part is for you.</span>
          </h2>
          <ul>
            <li style={{ '--c': 'var(--ta-sun)' } as CSSProperties}>
              <div>
                <b>Photos don’t stay</b>
                <span>Used only to draw your book, then deleted.</span>
              </div>
            </li>
            <li style={{ '--c': 'var(--ta-grass)' } as CSSProperties}>
              <div>
                <b>Never used to train AI</b>
                <span>Not your photos, not your voice, not your child’s details.</span>
              </div>
            </li>
            <li style={{ '--c': 'var(--ta-sky)' } as CSSProperties}>
              <div>
                <b>Location removed</b>
                <span>We strip the camera and GPS data from every upload.</span>
              </div>
            </li>
            <li style={{ '--c': 'var(--ta-pink)' } as CSSProperties}>
              <div>
                <b>You see it before you pay</b>
                <span>The first spread is free. No subscription, ever.</span>
              </div>
            </li>
          </ul>
        </section>

        <section className="ta-prices" aria-labelledby="ta-prices">
          <h2 id="ta-prices" className="ta-h2">
            Pick a book, <span className="ta-o">pay once</span>
          </h2>
          <p className="ta-lede">
            Every book includes the family link and a print-ready file.{' '}
            <Link to="/pricing">Compare everything →</Link>
          </p>
          <ul className="ta-tiers">
            {plans.map((plan) => (
              <li
                key={plan.id}
                className="ta-tier"
                data-popular={plan.id === POPULAR_PLAN ? 'true' : 'false'}
                style={{ '--c': tierColour[plan.id] } as CSSProperties}
              >
                <div className="ta-dot" />
                <h3>{plan.name}</h3>
                <div className="ta-amount">{plan.price}</div>
                <p>{summaries[plan.id](plan.characters)}</p>
              </li>
            ))}
          </ul>
          <p className="ta-fine">{brand.priceNotes['en'] ?? ''}</p>
        </section>

        <section className="ta-final" aria-labelledby="ta-final">
          <h2 id="ta-final" className="ta-h2">
            Ready, set,
            <br />
            <span className="ta-o">TaleAroo!</span>
          </h2>
          <p>The first spread is on us.</p>
          <Link to={createBook} className="ta-btn">
            Make their book
          </Link>
        </section>
      </main>
      <Footer page={page} />
    </div>
  );
}
